import React from 'react';

interface IQA {
  question: string;
  answer: string;
  score: number;
  justification: string;
}

interface IQAProps {
  data: IQA[];
}

export const QA: React.FC<IQAProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-16 px-4'>
        <div className='w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4'>
          <svg
              className='w-10 h-10 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-semibold text-gray-600 mb-2'>No Q&A Available</h3>
        <p className='text-sm text-gray-500 text-center max-w-md'>
          Interview questions and answers will appear here once they are recorded.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      { data.map((qa, index) => {
        // Color coding based on score
        let scoreColor = 'text-gray-500';
        let scoreBgGradient = 'from-gray-500 to-gray-600';
        let borderColor = 'border-gray-200';
        let bgGradient = 'from-gray-50 to-gray-100';
        let iconColor = 'text-gray-500';

        if (qa.score >= 80) {
          scoreColor = 'text-green-600';
          scoreBgGradient = 'from-green-500 to-emerald-600';
          borderColor = 'border-green-200';
          bgGradient = 'from-green-50 to-emerald-50';
          iconColor = 'text-green-500';
        } else if (qa.score >= 60) {
          scoreColor = 'text-blue-600';
          scoreBgGradient = 'from-blue-500 to-indigo-600';
          borderColor = 'border-blue-200';
          bgGradient = 'from-blue-50 to-indigo-50';
          iconColor = 'text-blue-500';
        } else if (qa.score >= 40) {
          scoreColor = 'text-orange-600';
          scoreBgGradient = 'from-orange-500 to-amber-600';
          borderColor = 'border-orange-200';
          bgGradient = 'from-orange-50 to-amber-50';
          iconColor = 'text-orange-500';
        } else {
          scoreColor = 'text-red-600';
          scoreBgGradient = 'from-red-500 to-rose-600';
          borderColor = 'border-red-200';
          bgGradient = 'from-red-50 to-rose-50';
          iconColor = 'text-red-500';
        }

        return (
          <div
              key={index}
              className={`group relative bg-white border ${borderColor} rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-200`}
          >
            { /* Header with Question Number and Score */ }
            <div className={`bg-gradient-to-r ${bgGradient} p-4 border-b ${borderColor} flex items-center justify-between`}>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 bg-gradient-to-br ${scoreBgGradient} rounded-xl flex items-center justify-center shadow-md`}>
                  <span className='text-white font-bold text-sm'>{ index + 1 }</span>
                </div>
                <div className='flex items-center gap-2'>
                  <svg className={`w-5 h-5 ${iconColor}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  <span className={`text-sm font-semibold ${scoreColor}`}>Question { index + 1 }</span>
                </div>
              </div>

              { /* Score Badge */ }
              <div className={`flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border ${borderColor}`}>
                <svg className={`w-4 h-4 ${iconColor}`} fill='currentColor' viewBox='0 0 20 20'>
                  <path d='M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z' />
                </svg>
                <span className={`font-bold ${scoreColor}`}>{ qa.score }%</span>
              </div>
            </div>

            { /* Question Section */ }
            <div className='p-5'>
              <div className='mb-4'>
                <div className='flex items-start gap-3'>
                  <div className='flex-shrink-0 mt-1'>
                    <div className='w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center'>
                      <svg className='w-5 h-5 text-purple-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                      </svg>
                    </div>
                  </div>
                  <div className='flex-1'>
                    <h4 className='text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2'>Question</h4>
                    <p className='text-gray-900 font-medium leading-relaxed'>{ qa.question }</p>
                  </div>
                </div>
              </div>

              { /* Answer Section */ }
              <div className='mb-4 pl-11'>
                <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100'>
                  <div className='flex items-start gap-2'>
                    <svg className='w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z' clipRule='evenodd' />
                    </svg>
                    <div className='flex-1'>
                      <h4 className='text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2'>Answer</h4>
                      <p className='text-gray-800 leading-relaxed'>{ qa.answer }</p>
                    </div>
                  </div>
                </div>
              </div>

              { /* Justification Section */ }
              { qa.justification && (
                <div className='pl-11'>
                  <div className={`bg-gradient-to-br ${bgGradient} rounded-xl p-4 border ${borderColor}`}>
                    <div className='flex items-start gap-2'>
                      <svg className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
                      </svg>
                      <div className='flex-1'>
                        <h4 className={`text-xs font-semibold ${scoreColor} uppercase tracking-wide mb-2`}>AI Assessment</h4>
                        <p className='text-gray-700 text-sm leading-relaxed'>{ qa.justification }</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) }
            </div>

            { /* Bottom gradient bar */ }
            <div className={`h-1 bg-gradient-to-r ${scoreBgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}></div>
          </div>
        );
      }) }
    </div>
  );
};
